/**
 * CLINICAL QUALITY METRICS MODEL
 * 
 * Evidence-based quality scoring (0-100 scale).
 * Components:
 *   1. Preventive Care (20%) - HEDIS compliance
 *   2. Chronic Disease Management (25%) - Disease control
 *   3. Access & Barriers (30%) - Cost-related skipped care
 *   4. Maternal & Infant Health (10%) - Infant mortality
 *   5. Provider Supply & Wait Times (15%) - Physician density
 * 
 * Ported from: quality_model.py
 */

const QUALITY_BASELINES = {
    // Component 1: Preventive Care
    preventive: {
        current_law: 0.67,
        safecare: 0.72, // Zero copay <200% FPL
        m4a: 0.78,      // Universal zero copay
        hr548: 0.67
    },
    // Component 2: Chronic Disease
    chronic: {
        current_law: 0.50,
        safecare: 0.58, // CGM + Nutrition
        m4a: 0.64,      // Full coverage
        hr548: 0.50
    },
    // Component 3: Access Barriers (Lower is better, so we invert)
    access_skip_rate: {
        current_law: 0.23,
        safecare: 0.08, // $750 cap
        m4a: 0.02,      // near zero
        hr548: 0.23
    },
    // Component 4: Maternal/Infant (Mortality reduction)
    maternal_impact: {
        current_law: 0.0,
        safecare: -0.7, // deaths/1000 reduction
        m4a: -1.8,
        hr548: 0.0
    }
};

/**
 * Calculate quality score (0-100) for a household under a specific reform scenario.
 * 
 * @param {Object} household - Household attributes (income, location, etc.)
 * @param {string} reformType - 'current_law', 'safecare', 'm4a', 'hr548'
 * @param {Object} countyData - Optional geographic context
 * @returns {number} - Quality score (0-100)
 */
export function calculateQualityScore(household, reformType, countyData = {}) {
    // Default to 'current_law' if unknown
    const scenario = QUALITY_BASELINES.preventive[reformType] ? reformType : 'current_law';

    // 1. Preventive Care (20%)
    let preventiveBase = QUALITY_BASELINES.preventive[scenario];
    // Poverty adjustment
    const fplPct = (household.income / 15000) || 3.0; // Rough proxy if FPL not calculated
    if (scenario === 'safecare' && fplPct < 2.0) preventiveBase += 0.05;
    if (scenario === 'm4a') preventiveBase += 0.03;

    const preventiveScore = preventiveBase * 100 * 0.20;

    // 2. Chronic Disease (25%)
    let chronicBase = QUALITY_BASELINES.chronic[scenario];
    if (household.age_head > 55) chronicBase += 0.05; // Older benefit more
    const chronicScore = chronicBase * 100 * 0.25;

    // 3. Access & Barriers (30%)
    let skipRate = QUALITY_BASELINES.access_skip_rate[scenario];
    if (fplPct < 1.5) skipRate += 0.05; // Poor access penalty
    if (fplPct > 4.0) skipRate -= 0.03; // Wealthy access bonus
    const accessScore = (1.0 - skipRate) * 100 * 0.30;

    // 4. Maternal & Infant (10%)
    const baselineMortality = 5.6;
    const targetMortality = 3.8;
    const impact = QUALITY_BASELINES.maternal_impact[scenario];
    const improvementPct = -impact / (baselineMortality - targetMortality);
    const maternalScore = (0.50 + Math.max(0, improvementPct) * 0.50) * 100 * 0.10;

    // 5. Provider Supply (15%) & Geographic Modifiers
    let providerBase = 50.0;

    // Geographic Adjustments
    // "Medical Desert" Logic
    let geoPenalty = 0;

    // Frontier Relief (SAFECare Specific - Sec 104)
    const isFrontier = countyData.is_frontier || household.is_frontier;
    let frontierRelief = (scenario === 'safecare' && isFrontier) ? 0.4 : 0.0; // 40% penalty reduction

    const density = countyData.population_density || household.population_density || 100;

    if (density < 20) {
        // Rural Penalty
        geoPenalty = -10.0 * (1.0 - frontierRelief);
    } else if (density > 1000) {
        // Urban Bonus
        geoPenalty = 5.0;
    }

    // Wait Time Penalty (M4A Specific)
    // Simplified: -3 points for surge
    if (scenario === 'm4a') {
        geoPenalty -= 3.0;
    }

    const providerScore = Math.max(0, providerBase + geoPenalty) * 0.15;

    // Total
    const totalScore = preventiveScore + chronicScore + accessScore + maternalScore + providerScore;

    return Math.min(100, Math.max(0, totalScore));
}
